import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

/**
 * SecurityGuard Component
 * 
 * Monitors session integrity and profile validation continuously
 * Forces logout if security violations are detected
 */
export function SecurityGuard() {
  const { user, signOut, isVisitor } = useAuth();

  useEffect(() => {
    if (!user?.id || isVisitor) return;

    let securityViolationCount = 0;
    const MAX_VIOLATIONS = 3;

    const validateUserSecurity = async () => {
      try {
        const { data: profileExists, error } = await supabase.rpc('validate_user_profile_integrity');
        
        if (error) {
          console.error('Security validation error:', error);
          securityViolationCount++;
        } else if (!profileExists) {
          console.error('SECURITY ALERT: User session detected without valid profile');
          
          await supabase.rpc('audit_sensitive_operation', {
            table_name: 'auth_users',
            operation: 'security_violation_detected',
            record_id: user.id,
            details: {
              violation_type: 'missing_profile',
              timestamp: new Date().toISOString(),
              user_agent: navigator.userAgent,
              url: window.location.href
            }
          });

          toast({
            variant: "destructive",
            title: "Sessão Inválida",
            description: "Sua sessão foi invalidada por motivos de segurança. Faça login novamente.",
          });
          
          await signOut();
          return;
        }

        if (profileExists) {
          securityViolationCount = 0;
        }

        if (securityViolationCount >= MAX_VIOLATIONS) {
          console.error('SECURITY ALERT: Maximum security violations reached');
          
          toast({
            variant: "destructive",
            title: "Problemas de Segurança",
            description: "Múltiplas falhas de validação detectadas. Faça login novamente.",
          });
          
          await signOut();
        }

      } catch (error) {
        console.error('Security validation failed:', error);
        securityViolationCount++;
      }
    };

    const initialTimeout = setTimeout(validateUserSecurity, 100);
    const interval = setInterval(validateUserSecurity, 15 * 60 * 1000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [user?.id, signOut, isVisitor]);

  useEffect(() => {
    if (!user?.id || isVisitor) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        try {
          await supabase.rpc('audit_sensitive_operation', {
            table_name: 'auth_users',
            operation: `auth_${event.toLowerCase()}`,
            record_id: session?.user?.id || user?.id,
            details: {
              event_type: event,
              timestamp: new Date().toISOString(),
              user_agent: navigator.userAgent
            }
          });
        } catch (error) {
          console.error('Failed to log auth event:', error);
        }
      }

      if (event === 'TOKEN_REFRESHED' && session?.user) {
        const { data: profileExists } = await supabase.rpc('validate_user_profile_integrity');
        if (!profileExists) {
          console.error('SECURITY ALERT: Token refreshed for user without profile');
          await signOut();
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [user?.id, signOut, isVisitor]);

  return null;
}
